/**
 * The inspector panel and the honesty overlay: every card, row, crumb and
 * refusal the Atlas draws as HTML beside the scene.
 *
 * Reads the closure it was cut out of through `InspectorDeps`, mostly as whole
 * handles, and writes back only `live.needsRender` and
 * `overlayView.state.notInSlice`. The `atlasCamera` probe members
 * that read nothing outside this seam ride on `probe`, which the composition
 * root in src/atlas.ts folds back into the one object the tools/preview/dojo
 * arms read.
 */

import type { Group } from 'three';

import { bandAt } from './camera.ts';
import type { Claim } from './claims';
import { corridorBandIndexAt, type CorridorGeometry } from './corridor.ts';
import { escapeHtml } from '../html.ts';
import type { ManifestReading } from './manifestPanel';
import {
  PV, arrayAreaFor, bandFor, dustOpticalDepth, softClimate, solarFluxWm2,
  sourceClass, sunAngles, type DataTier, type PlotClimate, type PlotField, type PlotMeta,
  type SourceClass, type SubPlot,
} from './plotField.ts';
import { parcelAreaKm2, parcelsIn, type PlotAddress } from './plotName.ts';
import { DUNE_CREST_HEIGHT_M } from './proceduralRelief.ts';
import { isPyramidPlot } from './pyramidPlot.ts';
import type { RockLaw } from './rockField';
import type { SurveyJob } from './rover.ts';
import type { SolPhotosphereReading } from './solPhotosphere';
import { all as allStructures } from './structures.ts';
import { formatEta, type CellBuilding } from './structureStatus.ts';
import { coverageAt, type CreditTier, type PyramidManifest } from './tileSource.ts';
import { albedoGsd, formatTileKey, gsd as heightGsd, tileForLatLon } from './tileTree.ts';
import { CABLE_M, cellYield } from './yield.ts';
import type { AtlasLive, AtlasState, Site } from '../atlas';
import type { BakeGlobalProduct, BodyRow, RegionalHeight } from '../world/types';
import type { SolarScrubHandle } from './solarScrub.ts';
import type { PyramidViewHandle } from './pyramidView.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { NavigationHandle } from './navigation.ts';
import type { DescentAnchorHandle } from './descentAnchor.ts';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { SurfaceRadiusHandle } from './surfaceRadius.ts';
import type { EventLogHandle } from './panels/eventLog.ts';
import type { SiteGroundHandle } from './siteGround.ts';
import type { CameraRigHandle } from './cameraRig.ts';
import type { OverlayViewHandle } from './overlayView.ts';
import type { PlotGeometryHandle } from './plotGeometry.ts';
import type { RoverDriveHandle } from './roverDrive.ts';
import type { SiteRegistryState } from './siteRegistry.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import type { DescentLoadHandle } from './descentLoad.ts';
import type { DojoPaintsHandle } from './dojoPaints.ts';

/**
 * The plot a `SubPlot` sits in, plus the res-8 parcel the cell itself falls
 * in. A plot covers a dozen or more parcels, and the parcel is the unit a
 * claim, a build and a survey are all keyed by, so `claim` is the claim over
 * `cellAddr`, not over the plot's own seat.
 */
export interface ParentPlot {
  place: PlotField['placements'][number];
  lat: number;
  lon: number;
  addr: PlotAddress;
  /** The selected cell's own parcel, null before h3-js has loaded. */
  cellAddr: PlotAddress | null;
  claim: Claim | null;
}

export interface HonestyReading {
  bodyId: string;
  displayName: string;
  /** The finest product baked per role, m/px, or the regional layer's posting under the aim. Null where none exists. */
  floor: {
    heightMpp: number | null; albedoMpp: number | null; instrument: string | null;
    /** The regional layer under the camera's aim whose posting heightMpp is, else null (the global floor). */
    heightLayer: string | null;
    /** The registry's own required acknowledgement for this product, verbatim.
     *  Null where the source asks for nothing beyond the bare rights notice. */
    credit: string | null;
  } | null;
  /** What the descent actually reaches. `pyramid` once the manifest is in,
   *  `registry` above the streaming edge, where the row's declaration is all
   *  there is to say, and `imagery` on a body with no elevation at all, whose
   *  answer is its row's at every altitude. */
  reach:
    {
      role: string; level: number; gsdM: number | null;
      from: 'pyramid' | 'registry' | 'imagery' | 'corridor' | 'shape';
    }
    | null;
  /** Where this body's own data stops the descent, metres. Null where nothing does. */
  floorAltitudeM: number | null;
  /** The corridor, on a class (c) row. Null on every other. */
  corridor: CorridorGeometry | null;
  /** The published shape model this body's surface IS, on a class (d) row. */
  shape: BakeGlobalProduct | null;
  coverage: { height: string; albedo: string };
  /**
   * What the imagery pyramid measured, off the pyramid rather than the row.
   *
   * `fraction` is the share of the SPHERE the mosaic covers; `here` is the
   * coverage mask's own cell under the camera's aim, at the level it states.
   * Null above the streaming edge, where no manifest has been fetched and
   * the honest answer is the row's words alone.
   */
  imagery: { fraction: number; here: number | null; hereLevel: number | null } | null;
  /**
   * The invented picture over ground nobody imaged, on a body that publishes
   * one. Null on every other, which is most of them.
   *
   * `share` is `1 - measured_fraction` off the open pyramid's own albedo
   * role, the whole sphere on a body whose row says the imagery is `no_data`,
   * and null above the streaming edge where neither statement is in hand -
   * the same silence `imagery` keeps there, rather than a guess. `shown` is
   * read off the globe's own uniform, so it is what the frame draws and not
   * what the switch says.
   */
  artificial: { share: number | null; basis: string; shown: boolean } | null;
  /**
   * The artificial RELIEF, which is a different statement from the imagery
   * above: how much relief the row's own field carries, what it was derived
   * from, and whether the frame is shading by it. Null on a body whose
   * unmeasured relief is left flat.
   */
  relief: { amplitudeM: number; capM: number; basis: string; shown: boolean } | null;
  /**
   * The Sun's live photosphere, and null on every other body.
   *
   * A SECOND artificial reading on the one body that carries two: the 304 A
   * map's own fill is the row above, and this is the sunspot record's region
   * list, whose artificial share at the mission clock is all of it.
   */
  sun: SolPhotosphereReading | null;
  descend: boolean;
  reason: string;
  /** The FIT, off the row. Null is a body the registry declares none for. */
  fit: string | null;
  /** How THIS ground's abundance was arrived at, off the plot's own bake.
   *  Null where no plot of this body's is loaded. */
  count: { source: string; kUsed: number; method: string } | null;
  /** Instances the scatter is drawing, where a plot is loaded. */
  scatter: number | null;
  /** The registry fields the LOADED ground stands in for. Only a plot built
   *  from the leaves can carry one: the claim path refuses a missing season
   *  rather than substituting. Null where nothing was stood in for. */
  standIn: readonly string[] | null;
  /** True where a missing season is not a guess: the body's sol spans its
   *  year, so the daily mean already comes off the orbit and the claim's own
   *  longitude (`PlotClimate.spinOrbit`) rather than an invented eccentricity. */
  standInWalksOrbit: boolean;
}

export interface InspectorDeps {
  readonly REFERENCE_MW: number;
  readonly CREDIT_WORD: Readonly<Record<CreditTier, string>>;
  readonly state: AtlasState;
  readonly overlay: HTMLElement;
  readonly elInspector: HTMLElement;
  readonly elHeroCta: HTMLElement;
  readonly tileGroup: Group;
  referenceWhSol(climate: PlotClimate): number;
  shortProduct(product: string): string;
  site(): Site | undefined;
  readonly dojoPaints: DojoPaintsHandle;
  readonly solarScrub: SolarScrubHandle;
  readonly pyramidView: PyramidViewHandle;
  readonly focusRig: FocusRigHandle;
  readonly navigation: NavigationHandle;
  readonly descentAnchorHandle: DescentAnchorHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly surfaceRadius: SurfaceRadiusHandle;
  readonly eventLogHandle: EventLogHandle;
  readonly siteGround: SiteGroundHandle;
  readonly cameraRig: CameraRigHandle;
  readonly overlayView: OverlayViewHandle;
  readonly plotGeometry: PlotGeometryHandle;
  readonly roverDrive: RoverDriveHandle;
  readonly siteRegistry: SiteRegistryState;
  readonly bodyScene: BodySceneHandle;
  readonly descentLoad: DescentLoadHandle;
  readonly live: AtlasLive;
  /** What stands on a cell, off the ground game (explore answers none). */
  cellBuildings(cell: string): CellBuilding[];
  openBuilding(buildingId: string): void;
}

export interface InspectorHandle {
  /** The `--honesty` levers, mutated in place by the probe's own setter. */
  readonly honestyDebug: { kSource: string | null; claimRelief: boolean; creditBlank: boolean };
  ringGainNote(row: BodyRow | undefined): { short: string; long: string } | null;
  rockLawFor(row: BodyRow | undefined): RockLaw | null;
  imageryCoverage(manifest: PyramidManifest | null):
    { fraction: number; here: number | null; hereLevel: number | null } | null;
  manifestReading(row: BodyRow): ManifestReading;
  renderHonestyRows(): string;
  renderPlotSampler(): string;
  renderParcel(cell: SubPlot): string;
  crumb(label: string, active: boolean, onClick?: () => void): string;
  ghostCrumb(label: string, tier: number): string;
  corridorCrumbs(): string[];
  snapInspectorFold(): void;
  tierHint(): string;
  syncHeroCta(): void;
  flashInspector(msg: string): void;
  clearFlash(): boolean;
  /** The sampler card's delegated click listener: section folds, the claim
   *  button, the survey dispatch and the tile "ENTER PLOT" button. */
  handleSamplerClick(e: MouseEvent): void;
  /** The `atlasCamera` members that read nothing outside this module. */
  readonly probe: {
    artificialReading(): { share: number | null; basis: string; shown: boolean } | null;
    reliefReading(): { amplitudeM: number; capM: number; basis: string; shown: boolean } | null;
    readonly honestyDebug: unknown;
  };
}

/** What each plot source class reads as on a card. */
const SOURCE_LABEL: Record<SourceClass, string> = {
  measured: 'measured',
  'measured+inferred': 'part measured',
  'coarse+synthetic': 'inferred',
};

/** A plot source's label, or the raw key for a class this build does not know.
 *  Never `measured`: an unrecognised provenance must not read as the strongest
 *  claim on the card. */
function sourceLabelFor(key: string | undefined): string {
  const cls = sourceClass(key);
  if (cls) return SOURCE_LABEL[cls];
  return key !== undefined && key !== '' ? key : 'unclassified';
}

export function createInspector(deps: InspectorDeps): InspectorHandle {
  const {
    REFERENCE_MW, CREDIT_WORD, state, overlay, elInspector, elHeroCta, tileGroup, referenceWhSol,
    shortProduct, site,
  } = deps;
  const { solar, solClockLabel, solarLatLon } = deps.solarScrub;
  const { tileMeshes } = deps.pyramidView;
  const { altitude } = deps.focusRig;
  const { applyState } = deps.navigation;
  const { availableQuad, displaySite, groundUnsurveyed } = deps.descentAnchorHandle;
  const {
    bodyClimate, bodyQuads, corridorFor, descendable, descentFloorM, floorTier, imageryFloor,
    quadFor, registryDatumM, rowById, shapeModelProduct,
  } = deps.bodyReaders;
  const { datumRadiusM, groundDatumM } = deps.surfaceRadius;
  const { logEvent, sect, toggleSection } = deps.eventLogHandle;
  const { parentOf, stakeSelected } = deps.siteGround;
  const { renderOverlay } = deps.overlayView;
  const { selectedCellLatLon } = deps.plotGeometry;
  const { setDriving } = deps.roverDrive;

  /**
   * The T5 panel. Every number here is measured off the same Float32Array the
   * shader displaces, so nothing in it can drift away from what is on screen.
   */
  /**
   * Where this claim's ground came from. Shown rather than implied: a player is
   * entitled to know which plots are measured and which are a defensible guess.
   */
  // ── the honesty overlay ────────────────────────────────────────────────────
  //
  // `data_floor` and `availability` are declared on every registry row and had
  // no consumer in `src/` at all: the descent stopped where the pyramid ran out
  // without ever saying what the finest real ground under it was, or what a
  // body with no product at all was showing instead. These rows are that
  // statement, live from ORBIT to SURFACE, and the only place the rock law's
  // provenance reaches a person.
  //
  // The LAW is per body, off the registry row's `bake.rocks`. The MEASUREMENT
  // is per bake, off the plot's or region's own `rocks` block, which says
  // whether this ground's abundance was counted (`measured`), inherited from
  // the site's median (`site-median`), or never counted because the body
  // carries no fit (`none`). The provenance wins where it exists, since it is
  // the narrower statement about the ground actually on screen; the row is
  // read where no plot is loaded, which is every band above the survey.

  /**
   * `descent-dojo --honesty --inject k-none` forces the reported source.
   * `--inject shape-relief` masks a class (d) row's own shape model out of
   * `honestyReading`, so its floor level falls through to the plain
   * "declared height" a body with real elevation reads. `--inject
   * credit-blank` blanks the focused product's own registry `credit` out of
   * the rendered overlay, the control for the `credit` criterion: a CC BY
   * product whose acknowledgement never reaches the page.
   */
  const honestyDebug: { kSource: string | null; claimRelief: boolean; creditBlank: boolean } = {
    kSource: null,
    claimRelief: false,
    creditBlank: false,
  };

  /**
   * What a ringed body's card and honesty overlay say about its brightness.
   *
   * One function, two surfaces, because the short row and the long sentence
   * disagreeing about the number would be worse than saying nothing. `short`
   * is the card's own row and `long` is the paragraph under it; both carry the
   * factor's own digits, and both are null on a row drawn exactly as measured
   * (Saturn), which gets no note at all.
   *
   * The stretch is on the reflected term alone - `uRingGain` in
   * src/atlas/rings.ts - so the radii, the widths, the gaps and the light the
   * ring takes out of whatever is behind it stay the measured profile, and the
   * words say so rather than leaving a player to assume it.
   */
  function ringGainNote(row: BodyRow | undefined):
    { short: string; long: string } | null {
    const rings = row?.rings;
    const g = rings?.brightness_gain;
    if (!rings || !g || deps.dojoPaints.ringDebug.suppressNote) return null;
    // What this system HAS and this engine does not draw, off the row's own
    // gap records. A component nobody can see is a hole in what a player is
    // told, so it is said in the same breath as the brightness.
    const km = (m: number): string => Math.round(m / 1000).toLocaleString('en-US');
    const gapShort = rings.gaps.map((x) => `${escapeHtml(x.name)} not drawn`).join('; ');
    const gapLong = rings.gaps.map((x) => ` The ${escapeHtml(x.name)} (${km(x.inner_m)} to `
      + `${km(x.outer_m)} km) is not drawn: ${escapeHtml(x.reason)} `
      + x.published.map((q) => escapeHtml(q)).join(' ')).join('');
    // One sourced sentence per component that carries one, so a system drawn
    // from several papers says which paper each stretch of it came from
    // rather than pointing a player at one block-level `source`.
    const citeLong = rings.components.filter((c) => c.cite)
      .map((c) => ` ${escapeHtml(c.cite!)}`).join('');
    const join = (a: string, b: string): string => (a && b ? `${a}; ${b}` : a || b);
    if (g.too_faint) {
      return {
        short: join('too faint to see - drawn as measured', gapShort),
        long: `${escapeHtml(row!.display_name)}'s rings are drawn as measured and are too faint `
          + 'to see: even lifted to a perfectly white ring of the same measured optical depth '
          + `they stay under the ${g.target_grey} grey levels over the sky beside them that a `
          + `ring has to clear to be visible here, so nothing is exaggerated for them.${gapLong}`
          + citeLong,
      };
    }
    if (g.factor === 1) {
      if (!gapShort && !citeLong) return null;
      return {
        short: gapShort,
        long: `${escapeHtml(row!.display_name)}'s rings are drawn exactly as `
          + `measured.${gapLong}${citeLong}`,
      };
    }
    const f = g.factor.toFixed(1);
    return {
      short: join(`brightness exaggerated ${f}x for visibility`, gapShort),
      long: `Ring brightness exaggerated ${f}x for visibility: the light the ring reflects `
        + `is drawn ${f} times what a particle albedo of ${rings.particle_albedo} gives, `
        + `which puts its brightest step ${g.target_grey} grey levels over the sky beside it. `
        + 'The radii, the widths, the gaps and the light the ring takes out of the planet and '
        + `the stars behind it are the measured occultation profile, untouched.${gapLong}${citeLong}`,
    };
  }

  /** The body's own Golombek-form fit. Null is a row that declares none. */
  function rockLawFor(row: BodyRow | undefined): RockLaw | null {
    const r = row?.bake?.rocks;
    if (!r || r.k_source === 'none' || r.a === null || r.b === null) return null;
    return { source: r.k_source, a: r.a, b: r.b };
  }


  /** The finest regional height layer the row lists over the aim, from LOCAL down where an aim is a place; else null. */
  function regionalHeightHere(row: BodyRow): RegionalHeight | null {
    const layers = row.data_floor?.regional_height;
    const band = deps.focusRig.band();
    if (!layers || band === 'SYSTEM' || band === 'ORBIT' || band === 'REGIONAL') return null;
    const cs = deps.focusRig.focusCam.state;
    const at = cs.latDeg !== null && cs.lonDeg !== null ? { lat: cs.latDeg, lon: cs.lonDeg } : solarLatLon();
    if (!at) return null;
    let best: RegionalHeight | null = null;
    for (const l of layers) {
      const k = tileForLatLon(row.id, (at.lat * Math.PI) / 180, (at.lon * Math.PI) / 180, l.root_level);
      if (!l.root_runs.some(([f, x, y0, y1]) => f === k.face && x === k.x && k.y >= y0 && k.y <= y1)) continue;
      if (best === null || l.height_mpp < best.height_mpp) best = l;
    }
    return best;
  }

  function honestyReading(): HonestyReading | null {
    const row = rowById(state.bodyId);
    if (!row) return null;
    // Only this body's own pyramid and plot. Both outlive a focus change by a
    // frame or two, and a panel reporting the last body's floor over the new
    // body's row would be the exact dishonesty it exists to prevent.
    const manifest = deps.pyramidView.tileBody === row.id ? deps.pyramidView.tileSource?.manifest() ?? null : null;
    const role = manifest?.roles.get('height') ?? manifest?.roles.get('albedo') ?? null;
    const roleName = manifest?.roles.has('height') === true ? 'height' : 'albedo';
    // An imagery-only body answers off its ROW at every band, not off the
    // manifest above the streaming altitude and the manifest below it: the
    // sentence a person is owed here does not change with the camera, and the
    // bake's own `--check` is what holds the two to the same floor level.
    const floor = imageryFloor(row.id);
    // A cloud-top corridor answers off the row at every band too, and its
    // answer is that there is no surface at all - no level and no ruler,
    // because neither exists to quote.
    const corridor = corridorFor(row.id);
    // A shape model answers off the row at every band too, and its answer is
    // that the surface is a published shape and nothing under it was ever
    // measured as relief. `claimRelief` masks it out for the control.
    const shape = honestyDebug.claimRelief ? null : shapeModelProduct(row.id);
    const shapeLevel = shape && row.bake ? row.bake.height_floor_level : null;
    const reach = corridor
      ? { role: 'cloud-top', level: corridor.bands.length, gsdM: null, from: 'corridor' as const }
      : floor
      ? { role: 'albedo', level: floor.level, gsdM: floor.gsdM, from: 'imagery' as const }
      : shape && shapeLevel !== null
      ? {
        role: 'height',
        level: shapeLevel,
        // On the sphere the shape pyramid is relief ABOUT, which is the row's
        // mean radius rather than its long semi-axis: `bake_pyramid.py` takes
        // that datum from the height row's own `value_offset`, so quoting the
        // ellipsoid a_m here would print a cell 26 per cent coarser than the
        // one pyramid.json publishes.
        gsdM: heightGsd(shapeLevel, datumRadiusM(row.id) ?? row.radius_m),
        from: 'shape' as const,
      }
      : role
        ? {
          role: roleName,
          level: role.floor_level,
          gsdM: role.levels.get(role.floor_level)?.gsd_m ?? null,
          from: 'pyramid' as const,
        }
        : row.bake && row.bake.height_floor_level !== null
          ? { role: 'height', level: row.bake.height_floor_level, gsdM: null, from: 'registry' as const }
          : null;
    // Whose ground the LOADED plot is, off its own slug rather than off the
    // site list, which a focus change re-derives before the plot is released.
    const loadedFor = deps.live.plotField ? deps.siteRegistry.SITES.find((st) => st.id === deps.live.plotField?.meta.slug)?.body : undefined;
    const mine = loadedFor === row.id;
    const rk = mine ? deps.live.plotField?.meta.rocks ?? null : null;
    const forced = honestyDebug.kSource;
    const rowLaw = rockLawFor(row);
    // Two different facts under one field name. The ROW's `bake.rocks.k_source`
    // names the fit, which is the only thing that can be attributed; the PLOT's
    // says how this ground's own k was arrived at. The provenance is the
    // narrower statement, so its `none` withdraws the row's fit - that is the
    // bake reporting it never counted anything here.
    const fit = forced === 'none' || rowLaw === null || rk?.k_source === 'none'
      ? null
      : rowLaw.source;
    // What the ground under the camera stood in for, off the same `softClimate`
    // the build took its light from.
    const soft = deps.live.plotField && isPyramidPlot(deps.live.plotField.meta) && deps.descentLoad.state.pyramidPlotBody === row.id
      ? softClimate(row, rowById)
      : null;
    const standIn = soft?.missing ?? [];
    const layer = row.data_floor?.height_mpp != null ? regionalHeightHere(row) : null;
    // The layer's own deepest level is what the descent reaches over it, so the reach row and the floor row describe the same ground.
    const layerReach = layer && reach && reach.role === 'height' && (reach.from === 'pyramid' || reach.from === 'registry')
      ? { role: 'height', level: layer.level, gsdM: heightGsd(layer.level, datumRadiusM(row.id) ?? row.radius_m), from: reach.from }
      : null;
    return {
      bodyId: row.id,
      displayName: row.display_name,
      floor: row.data_floor
        ? {
          heightMpp: layer ? layer.height_mpp : row.data_floor.height_mpp,
          heightLayer: layer ? shortProduct(layer.product) : null,
          albedoMpp: row.data_floor.albedo_mpp,
          instrument: row.data_floor.instrument,
          credit: row.data_floor.credit,
        }
        : null,
      reach: layerReach ?? reach,
      floorAltitudeM: descentFloorM(row.id),
      corridor,
      shape,
      coverage: { height: row.availability.coverage.height, albedo: row.availability.coverage.albedo },
      imagery: imageryCoverage(manifest),
      artificial: artificialReading(row, manifest),
      relief: reliefReading(row),
      sun: row.id === 'sol' ? deps.bodyScene.state.orrery?.solPhotosphereReading() ?? null : null,
      descend: row.availability.descend,
      reason: row.availability.reason,
      fit,
      count: rk ? { source: forced ?? rk.k_source, kUsed: rk.k_used, method: rk.method } : null,
      scatter: mine && deps.live.plot ? deps.live.plot.rocks?.count ?? 0 : null,
      standIn: standIn.length > 0 ? standIn : null,
      standInWalksOrbit: !!soft?.climate.spinOrbit,
    };
  }

  /**
   * What this body's artificial fill covers, what it was derived from, and
   * whether the frame is drawing it.
   *
   * The share is never taken from the `artificial` block: that block says how
   * the picture was MADE, and how much of the sphere it covers is the measured
   * role's own statement about what it missed.
   */
  function artificialReading(row: BodyRow, manifest: PyramidManifest | null):
    { share: number | null; basis: string; shown: boolean } | null {
    const art = row.artificial;
    if (art === undefined) return null;
    // A `globe_map` body has no pyramid to read and never will: its measured
    // product IS the globe map, and that map's own bake published the share.
    const measured = row.availability.coverage.albedo === 'globe_map'
      ? row.bake?.stamp?.measured_fraction ?? null
      : manifest?.roles.get('albedo')?.coverage?.fraction ?? null;
    const share = row.availability.coverage.albedo === 'no_data'
      ? 1 : measured === null ? null : 1 - measured;
    // The basis's lead clause: the whole sentence carries the fitted numbers
    // too, and this row is one line beside ten others. The full text is in the
    // registry and in the pyramid's own role block.
    const lead = art.basis.split(':')[0] ?? art.basis;
    return { share, basis: lead, shown: deps.bodyScene.state.orrery?.artificialDrawn(row.id) ?? false };
  }

  /**
   * This body's artificial relief: the amplitude the bake published, the cap it
   * was held to, its basis and whether the frame is shading by it.
   *
   * The amplitude comes off the row and not off a manifest, because this field
   * is not in any pyramid: it is one map beside the globe's own.
   */
  function reliefReading(row: BodyRow):
    { amplitudeM: number; capM: number; basis: string; shown: boolean } | null {
    const art = row.artificial_height;
    if (art === undefined) return null;
    // A `classes` row's dunes are the tallest relief the generator draws
    // (`DUNE_CREST_HEIGHT_M`), held to the same 2,000 m cap a baked row is.
    if (art.classes !== undefined) {
      return {
        amplitudeM: DUNE_CREST_HEIGHT_M,
        capM: 2000,
        basis: 'imagery-conditioned',
        shown: reliefShownNow(row.id),
      };
    }
    if (art.relief_m === undefined || art.relief_cap_m === undefined) return null;
    const lead = art.basis.split(':')[0] ?? art.basis;
    return {
      amplitudeM: art.relief_m,
      capM: art.relief_cap_m,
      basis: lead,
      shown: reliefShownNow(row.id),
    };
  }

  /**
   * Whether the frame is shading by this body's relief, read off what is
   * drawing the ground.
   *
   * The globe's uniform is the whole answer only while the globe is the
   * picture. Past the streaming edge the pyramid is, and a tile whose material
   * carries no relief chunk shades by none however that uniform stands - which
   * is what this row used to report as "shown" over ground that had gone flat.
   */
  function reliefShownNow(id: string): boolean {
    if (!(deps.bodyScene.state.orrery?.artificialReliefDrawn(id) ?? false)) return false;
    if (deps.pyramidView.tileBody !== id || !tileGroup.visible || tileMeshes.size === 0) return true;
    return [...tileMeshes.values()].some((h) => h.reliefShaded || h.reliefDisplaced);
  }

  /**
   * The open pyramid's own imagery coverage, and the mask's answer where the
   * camera is aimed.
   *
   * The solar place over which the sun is read: on a siteless body,
   * solarLatLon() falls back to focusCam.state; the coverage fraction and its
   * detail band are queried at that place. Where a manifest is resident and
   * altitude() <= tileStreamAltM(), this is queried; solarSite is non-null
   * only above the ORBIT edge where the ladder is untethered from ground. A
   * pyramid cut before coverage was data publishes none and this is null,
   * which the row then says rather than guessing 100%.
   */
  function imageryCoverage(manifest: PyramidManifest | null):
    { fraction: number; here: number | null; hereLevel: number | null } | null {
    const cov = manifest?.roles.get('albedo')?.coverage ?? null;
    if (!manifest || !cov) return null;
    const at = solarLatLon();
    const here = at
      ? coverageAt(manifest, 'albedo', (at.lat * Math.PI) / 180, (at.lon * Math.PI) / 180)
      : null;
    return {
      fraction: cov.fraction,
      here: here ? here.fraction : null,
      hereLevel: here ? here.level : null,
    };
  }

  const mpp = (v: number | null): string => (v === null ? 'no product' : `${v.toFixed(v < 1 ? 2 : 0)} m/px`);

  /**
   * One manifest row's derived facts, in the words the honesty overlay and the
   * credit label already use.
   *
   * Read off the ROW and never off the open pyramid: the panel lists every body
   * and at most one of them has tiles on screen, so a floor taken from the
   * resident manifest would be the focused body's answer printed against twenty
   * other rows - the same dishonesty `honestyReading` guards against.
   */
  function manifestReading(row: BodyRow): ManifestReading {
    const isCorridor = row.corridor !== null;
    const floor = imageryFloor(row.id);
    const stopM = descentFloorM(row.id);
    const shape = shapeModelProduct(row.id);
    const chip = isCorridor
      ? 'corridor'
      : floor !== null
        ? stopM === null ? 'smooth ground' : 'imagery floor'
        : shape !== null
          ? 'shape model'
          : row.availability.descend
            ? 'ground to SURFACE'
            : 'orbit only';
    // The rung the descent stops on. A body whose own data stops it nowhere
    // above the ground stops at the eye floor, SURFACE, only if it descends
    // at all; camera.ts's minAltitude falls back to ORBIT otherwise.
    const rung = stopM === null
      ? (row.availability.descend ? 'SURFACE' : 'ORBIT')
      : bandAt(stopM, registryDatumM(row.id));
    const level = isCorridor
      ? `${row.corridor?.bands.length ?? 0} cloud-top bands, no surface`
      : floor !== null
        ? `albedo L${floor.level} \u00b7 ${mpp(floor.gsdM)}`
        : row.bake && row.bake.height_floor_level !== null
          ? `height L${row.bake.height_floor_level} \u00b7 ${mpp(row.data_floor?.height_mpp ?? null)}`
          : 'no pyramid';
    const credit = row.bake && row.bake.global.length > 0
      ? row.bake.global
        .map((g) => `${g.role === 'height' ? CREDIT_WORD.terrain : CREDIT_WORD.imagery} `
          + `${shortProduct(g.product)}`)
        .join(' \u00b7 ')
      : row.data_floor?.instrument ?? 'no product for this body';
    const rights = row.bake?.global[0]?.rights ?? row.data_floor?.rights ?? null;
    return {
      chip,
      floor: `${rung} \u00b7 ${level}`,
      credit: rights === null ? credit : `${credit} \u00b7 ${rights}`,
    };
  }

  /**
   * The rows themselves, hosted by whichever panel is up: the body card above
   * the survey and the sampler's EVIDENCE section at and below it, which is
   * where the inspector gives its column away at 800x500 (index.html's small
   * -pane rules). One host at a time, so the ids stay unique and the arm reads
   * the same four selectors at every band.
   */
  /**
   * What the coverage mask says about the cell the camera is aimed at.
   *
   * The GRANULARITY is stated rather than implied: the mask is one byte per
   * tile at its own level, so "none measured" is a statement about that cell
   * and not about the metre under the reticle. The tile's alpha carries the
   * per-texel truth for anything that needs it.
   */
  function imageryHereText(here: number, level: number): string {
    const row = rowById(state.bodyId);
    const radius = row ? row.radius_m : 0;
    const km = Math.round((albedoGsd(level, radius) * 512) / 1000);
    const cell = `L${level} cell, ${km} km`;
    if (here <= 0.002) return `no imagery &middot; never measured &middot; ${cell}`;
    if (here >= 0.998) return `measured &middot; ${cell}`;
    return `${(here * 100).toFixed(0)}% measured &middot; ${cell}`;
  }

  /** The full sentence: the row's tooltip, not its value - the panel is too
   *  narrow to print the basis clause and still fit in two lines. */
  function artificialTip(a: {
    share: number | null; basis: string; shown: boolean;
  }): string {
    const share = a.share === null
      ? 'share unread above the streaming edge'
      : `${(a.share * 100).toFixed(1)}% of the sphere`;
    return `${share}, ${a.basis}, ${a.shown ? 'shown' : 'hidden'}`;
  }

  function artificialText(a: {
    share: number | null; basis: string; shown: boolean;
  }): string {
    const share = a.share === null ? 'unread' : `${(a.share * 100).toFixed(1)}%`;
    // A one-word code, not the lead clause itself: the lead runs to 188
    // characters on a banded gap fill (Saturn, Neptune), which the tooltip
    // above carries in full and this row cannot.
    const code = a.basis.split(' ')[0] ?? a.basis;
    return `${share} · ${code} · ${a.shown ? 'shown' : 'hidden'}`;
  }

  function reliefText(r: {
    amplitudeM: number; capM: number; basis: string; shown: boolean;
  }): string {
    return `${Math.round(r.amplitudeM)} m of relief on ground no altimeter reached, `
      + `${r.basis}, ${r.shown ? 'shown' : 'hidden'}`;
  }

  /**
   * The Sun's live photosphere, in the row's own short form.
   *
   * It names what the disc is drawing rather than what the switch asks for: a
   * count of regions, how many of those are modelled, and whether the live
   * layer or the 304 A composite is the picture on screen.
   */
  function sunPhotosphereText(s: SolPhotosphereReading): string {
    if (!s.live) return 'off &middot; the AIA 304 A composite of CR 2314 is drawn instead';
    if (!s.loaded) return s.error === null ? 'loading the sunspot record' : 'record unread';
    const modelled = s.artificialCount === 0
      ? 'all measured'
      : s.measuredCount === 0
        ? 'all modelled'
        : `${s.artificialCount} of ${s.measuredCount + s.artificialCount} modelled`;
    const held = s.artificialOn ? '' : ' &middot; modelled regions off';
    const cut = s.dropped === 0 ? '' : ` &middot; ${s.dropped} under the cap`;
    return `${s.drawnCount} region${s.drawnCount === 1 ? '' : 's'} drawn &middot; ${modelled}${held}${cut}`;
  }

  /**
   * The credit clause of a source's rights text: up to its first sentence end.
   *
   * The rest of that text is the licence in the distributor's own words, which
   * belongs in the artifact and in docs/ATLAS-TERRAIN.md rather than in a card
   * this size. Taken from the artifact rather than written here, so a re-bake
   * against another source cannot leave a stale credit on screen.
   */
  function sourceCredit(rights: string): string {
    const end = rights.indexOf('. ');
    return end < 0 ? rights : rights.slice(0, end + 1);
  }

  function renderHonestyRows(): string {
    const h = honestyReading();
    if (!h) return '';
    const floorText = h.floor
      ? `${mpp(h.floor.heightMpp)} height${h.floor.heightLayer ? ` (${escapeHtml(h.floor.heightLayer)})` : ''} &middot; ${mpp(h.floor.albedoMpp)} albedo`
      : 'no product for this body';
    const reachText = h.reach === null
      ? 'no pyramid'
      : h.reach.from === 'corridor'
        ? 'cloud-top corridor, no surface'
        : h.reach.from === 'imagery'
        ? `imagery only, no elevation &middot; ${h.reach.role} L${h.reach.level} `
          + `&middot; ${mpp(h.reach.gsdM)}`
        : h.reach.from === 'shape'
        ? `shape model, no measured relief &middot; ${h.reach.role} L${h.reach.level} `
          + `&middot; ${mpp(h.reach.gsdM)}`
        : h.reach.from === 'pyramid'
          ? `${h.reach.role} L${h.reach.level} &middot; ${mpp(h.reach.gsdM)}`
          : `declared ${h.reach.role} L${h.reach.level}`;
    const drawn = h.scatter === null ? '' : ` &middot; ${h.scatter} drawn`;
    const lawText = h.fit === null
      ? `none &middot; no fit for this body${drawn}`
      : `${escapeHtml(h.fit)}${h.count ? ` &middot; ${escapeHtml(h.count.source)} k `
        + `${h.count.kUsed.toFixed(4)}` : ''}${drawn}`;
    // Which rung of 04-body-data-ladder.md's classes this body is on, where the
    // answer is that there is no elevation to descend into at all.
    const km = (m: number): string => `${Math.round(m / 1000)}`;
    const classNote = h.corridor !== null
      ? `${escapeHtml(h.displayName)} has a cloud-top corridor and no surface: three bands over `
        + `an opaque deck ${km(h.corridor.deckAltitudeM)} km above the datum - `
        + `${h.corridor.bands.map((b) => b.title).join(', ')} - and the descent stops at the `
        + `lowest band's own edge, ${km(h.corridor.floorAltitudeM)} km above the datum. `
      : h.reach?.from === 'imagery' && h.floorAltitudeM !== null
      ? `${escapeHtml(h.displayName)} has imagery and no elevation: the surface is the `
        + 'ellipsoid and the descent stops at the imagery floor, '
        + `${(h.floorAltitudeM / 1000).toFixed(0)} km above the datum. `
      : h.reach?.from === 'imagery'
      ? `${escapeHtml(h.displayName)} has imagery and no elevation: the ground you stand on `
        + 'is a smooth sphere at the datum under that imagery, with no relief on it. '
      : h.reach?.from === 'shape' && h.shape !== null
      ? `${escapeHtml(h.displayName)} is a shape model and nothing more: no raster `
        + 'elevation and no close-up imagery were ever published for it, so the drawn '
        + `surface is ${escapeHtml(h.shape.product)} at ${mpp(h.floor?.heightMpp ?? null)}, `
        + 'sampled along each direction from the body centre. It is the shape that was '
        + 'measured, not the relief on it, and there is no ground to descend onto. '
      : '';
    // What a person is owed when the answer is "nothing was measured here".
    const note = h.fit === null
      ? `No rock-abundance law has been fitted for ${escapeHtml(h.displayName)}: nothing is `
        + 'scattered and no clast lag is drawn, rather than reading a count off another '
        + "body's coefficients."
      : h.count
        ? `Abundance under the ${escapeHtml(h.fit)} fit, ${escapeHtml(h.count.method)}.`
        : `Abundance will be counted per plot under the ${escapeHtml(h.fit)} fit.`;
    const below = h.reach && h.reach.from === 'pyramid' && h.reach.gsdM !== null
      ? ` Below ${mpp(h.reach.gsdM)} the ground is that level resampled, except inside a `
        + "detail region's own leaves."
      : '';
    // The Sun's own note, which is the record's three fixed strings; the
    // sentence after it is what the ARTIFICIAL FILL row does to that answer,
    // since a disc with the layer off is spotless for a reason a player is owed.
    const sunBase = ' The regions are painted over the same 304 A frame this disc '
      + 'draws with LIVE SUN off, so the picture under them is measured and what moves '
      + 'with the clock on top of it is the record.';
    const sunNote = h.sun === null || !h.sun.live
      ? ''
      : h.sun.artificialOn || h.sun.artificialCount === 0
        ? `${h.sun.note}${sunBase}`
        : `${h.sun.note} The ARTIFICIAL FILL layer is off, so none of those ${
          h.sun.artificialCount} is drawn and this disc carries the measured record alone.${sunBase}`;
    // The registry gap the ground under the camera was built over, which only
    // a plot resampled from the leaves can have: the season row further down
    // this card reads like a fitted one and this is what says it is not.
    const standInText = h.standIn === null ? '' : (() => {
      const label: Record<string, string> = {
        season: 'season',
        'rotation.solar_day_s': 'solar day',
        solar_flux_mean_W_m2: 'mean irradiance',
      };
      const listed = h.standIn.map((f) => label[f] ?? f).join(', ');
      return `no ${listed} for ${escapeHtml(h.displayName)} &middot; ${h.standIn.includes('season')
        ? (h.standInWalksOrbit
          ? 'the daily mean follows the orbit and the longitude here; the momentary brightness does not swing yet'
          : 'a mean-distance, zero-declination season lights this ground')
        : "the light here stands on defaults, not on this body's own numbers"}`;
    })();
    // The ring sentence rides in the same hint paragraph the class note and the
    // rock law use, off the same function the card's own row reads.
    const ringNote = ringGainNote(rowById(h.bodyId));
    // The full sentence goes in the row's tooltip; the panel is too narrow to
    // print it and still fit in two lines, so the value itself is the coded
    // form the sentence is built from.
    const coverageTip = `${h.descend ? 'descend' : 'no descent'} · height ${h.coverage.height}`
      + ` · albedo ${h.coverage.albedo}${h.imagery === null ? ''
        : ` · imagery ${(h.imagery.fraction * 100).toFixed(1)}% of the sphere`}`;
    const coverageText = `${h.descend ? 'descend' : 'no descent'} &middot; ${h.coverage.height}/${h.coverage.albedo}`
      + (h.imagery === null ? '' : ` &middot; ${(h.imagery.fraction * 100).toFixed(0)}%`);
    return `${ringNote === null ? ''
      : `<div class="atlas-card-hint" id="hon-rings">${ringNote.long}</div>`}
      <div class="atlas-card-row"><span>data floor</span><span id="hon-floor">${floorText}</span></div>
      <div class="atlas-card-row"><span>pyramid reach</span><span id="hon-reach">${reachText}</span></div>${
        h.floor?.heightLayer && h.reach?.gsdM != null
          ? `<div class="atlas-card-hint" id="hon-layer-note">Data floor is how far apart the ${
            escapeHtml(h.floor.heightLayer)} height measurements under you are; pyramid reach is the finest cell the ground is drawn in, which can be finer than the spacing because the layer interpolates between measurements.</div>`
          : ''}
      <div class="atlas-card-row hud-tip" data-tooltip="${escapeHtml(coverageTip).replace(/"/g, '&quot;')}">
        <span>coverage</span><span id="hon-cover">${coverageText}</span></div>${
      h.imagery === null || h.imagery.here === null ? ''
        : `<div class="atlas-card-row"><span>imagery here</span><span id="hon-imagery">${
          imageryHereText(h.imagery.here, h.imagery.hereLevel ?? 0)}</span></div>`}${
        h.artificial === null ? ''
          : `<div class="atlas-card-row hud-tip" data-tooltip="${
            escapeHtml(artificialTip(h.artificial)).replace(/"/g, '&quot;')}">
            <span>artificial fill</span><span id="hon-artificial">${
              escapeHtml(artificialText(h.artificial))}</span></div>`}${
        h.relief === null ? ''
          : `<div class="atlas-card-row"><span>artificial relief</span><span id="hon-relief">${
            escapeHtml(reliefText(h.relief))}</span></div>`}${
        h.sun === null ? ''
          : `<div class="atlas-card-row"><span>photosphere</span><span id="hon-sun">${
            sunPhotosphereText(h.sun)}</span></div>`}${
        sunNote === '' ? ''
          : `<div class="atlas-card-hint" id="hon-sun-note">${escapeHtml(sunNote)}</div>`}${
        h.sun === null || !h.sun.live || h.sun.source === null ? ''
          : `<div class="atlas-card-hint" id="hon-sun-source">${escapeHtml(
            `The sunspot record: ${sourceCredit(h.sun.source.rights)} ${
              h.sun.source.range}, retrieved ${h.sun.source.retrieved}.`)}</div>`}${
        standInText === '' ? ''
          : `<div class="atlas-card-row"><span>stand-in</span><span id="hon-standin">${
            standInText}</span></div>`}
      <div class="atlas-card-row"><span>rock law</span><span id="hon-rocks">${lawText}</span></div>
      <div class="atlas-card-hint" id="hon-note">${classNote}${note}${below}</div>
      ${h.floor?.instrument ? `<div class="atlas-card-hint" id="hon-source">${
        escapeHtml(h.floor.instrument)}</div>` : ''}${(() => {
        // The product's own required acknowledgement, printed small under it -
        // the raster floor's credit where one is baked, else the shape model's,
        // never composed and never a second panel.
        const credit = honestyDebug.creditBlank ? null : (h.floor?.credit ?? h.shape?.credit ?? null);
        return credit
          ? `<div class="atlas-card-hint" id="hon-credit">${escapeHtml(credit)}</div>`
          : '';
      })()}`;
  }

  function renderProvenanceRows(m: PlotMeta): string {
    const by = m.hex.plots_by_source;
    if (!by) return '';
    const rows = Object.entries(by)
      .map(
        ([k, n]) =>
          `<div class="atlas-card-row"><span>${escapeHtml(sourceLabelFor(k))}</span><span>${n} plot${n === 1 ? '' : 's'}</span></div>`,
      )
      .join('');
    return rows;
  }

  /**
   * The T5/T6 panel on a body with no surveyed site.
   *
   * Its PLOT and SURFACE ground is the global pyramid's floor level, resampled
   * below that level's own sampling, so nothing here is missing and nothing is
   * cached elsewhere. Naming the level and its ground sample is the honest
   * answer; "PLOT NOT CACHED" blamed an absent detail bake for ground that is
   * on screen.
   */
  function renderPyramidFloorCard(): string {
    const h = honestyReading();
    const reach = h?.reach ?? null;
    const name = h?.displayName ?? state.bodyId ?? 'This body';
    const standing = reach && reach.from === 'pyramid'
      ? `${reach.role.toUpperCase()} L${reach.level} &middot; ${mpp(reach.gsdM).toUpperCase()}`
      : reach && reach.from === 'registry'
        ? `DECLARED ${reach.role.toUpperCase()} L${reach.level}`
        : 'NO PYRAMID';
    return `<div class="atlas-card sampler">
      <div class="atlas-card-title">PYRAMID FLOOR</div>
      <div class="atlas-card-canon">${standing} &middot; NO DETAIL SITE</div>
      ${renderHonestyRows()}
      <div class="atlas-card-hint">${escapeHtml(name)} has no surveyed region, so no claim was
        asked for and none is missing: the ground here is the global pyramid at its finest level.
        Cut one with <code>python3 tools/dtm/bake_region.py --body ${
        escapeHtml(state.bodyId ?? '')} --slug &lt;slug&gt;</code>.</div>
    </div>`;
  }

  function renderPlotSampler(): string {
    // Nothing is on the wire during a pass over the leaves: the patch is being
    // resampled from tiles the descent already streamed, and the card that
    // says "fetching" here names a wait the player is not having.
    if (deps.descentLoad.state.pyramidPlotBuilding) {
      const who = honestyReading()?.displayName ?? state.bodyId ?? 'This body';
      return `<div class="atlas-card sampler">
        <div class="atlas-card-title">BUILDING GROUND</div>
        ${renderHonestyRows()}
        <div class="atlas-card-hint">${escapeHtml(who)} has no surveyed region, so the ground
          under you is being built out of the height tiles this descent already has, at the
          finest level they answer at. Nothing is being fetched.</div>
      </div>`;
    }
    if (deps.descentLoad.state.plotLoading) {
      // The site's own detail source off this body's manifest, never a Mars
      // product: every body with a claim renders this card.
      const detail = deps.pyramidView.tileBody === state.bodyId
        ? deps.pyramidView.tileSource?.manifest()?.extensions.find((e) => e.id === displaySite()?.id)?.source ?? null
        : null;
      const what = detail ? `${escapeHtml(detail.instrument)} ` : '';
      return `<div class="atlas-card sampler">
        <div class="atlas-card-title">ACQUIRING TERRAIN</div>
        ${renderHonestyRows()}
        <div class="atlas-card-hint">Fetching ${what}elevation…</div>
      </div>`;
    }
    // A body with no row in `sites.json` never asked for a claim, so there is
    // no bake to be missing: the ground under the camera is the pyramid's own
    // floor, and this says which level that is.
    // Only while there is no plot: a field built from this body's own leaves
    // IS the ground under the camera, and the card below names what it was
    // resampled from. The floor card is the answer for a siteless body the
    // ring has nothing resident for.
    if (deps.siteRegistry.sitesLoaded && site() === undefined
      && !(deps.live.plotField && isPyramidPlot(deps.live.plotField.meta))) return renderPyramidFloorCard();
    const climate = deps.live.plot?.climate ?? bodyClimate();
    if (deps.descentLoad.state.plotError || !deps.live.plotField || !climate) {
      // A fresh clone has the regions but not the per-plot tiles, so this is a
      // normal state rather than a fault. Say what to do about it.
      const isTile = state.tier >= 6;
      return `<div class="atlas-card sampler warn">
        <div class="atlas-card-title">${isTile ? 'PLOT NOT CACHED' : 'NO TERRAIN'}</div>
        <div class="atlas-card-row">${escapeHtml(deps.descentLoad.state.plotError ?? 'unavailable')}</div>
        ${renderHonestyRows()}
        <div class="atlas-card-hint">${
          isTile
            ? 'Detail tiles are not kept in the repo. Point <code>VITE_DTM_TILE_BASE</code> at <code>https://play.dev.4xciv.com/dtm</code>, or run <code>python3 tools/dtm/rebake.py ' +
              escapeHtml(site()?.id ?? '') +
              '</code> to cut them locally.'
            : 'Run <code>python3 tools/dtm/rebake.py</code> to bake the sites in <code>sites.json</code>.'
        }</div>
      </div>`;
    }

    const m = deps.live.plotField.meta;
    const counts: Record<string, number> = { prime: 0, workable: 0, marginal: 0, unbuildable: 0 };
    for (const p of deps.live.plotSubs) counts[p.verdict] = (counts[p.verdict] ?? 0) + 1;
    const best = deps.live.plotSubs.reduce<SubPlot | null>(
      (a, b) => (a === null || b.score > a.score ? b : a),
      null,
    );
    const dec = deps.solarScrub.sunDeclinationDeg(climate);
    const H = (solar.solPhase - 0.5) * 2 * Math.PI;
    const { alt, az } = sunAngles(m.center_lat_deg, dec, H);
    const flux = solarFluxWm2(solar.lsDeg, climate);
    const tau = dustOpticalDepth(solar.lsDeg, climate.dust);
    const buildable = counts['prime']! + counts['workable']!;

    const exagBadge = (deps.live.plot?.exaggeration ?? solar.exag) !== 1.0 ? ` <span class="atlas-dim">(relief &times;${(deps.live.plot?.exaggeration ?? solar.exag).toFixed(1)})</span>` : '';

    // A plot resampled from the leaves has no site name and no quadrangle
    // bake behind it: what names it is the body and the level it was read at.
    const fromPyramid = isPyramidPlot(m);
    const title = fromPyramid
      ? `${honestyReading()?.displayName ?? state.bodyId ?? 'GROUND'} GROUND`
      : displaySite()?.name ?? 'REGION';
    const canonLead = fromPyramid
      ? `PYRAMID L${deps.descentLoad.state.pyramidPlotLevel ?? 0} &middot; ${mpp(m.source_resolution_m_per_px).toUpperCase()}`
      : escapeHtml((displaySite()?.quad ?? '').toUpperCase());
    return `
      <div class="atlas-card sampler canon">
        <div class="atlas-card-title">${escapeHtml(title.toUpperCase())}${state.tileId ? ` &middot; ${escapeHtml(state.tileId.replace('_', ', '))}` : ''}</div>
        <div class="atlas-card-canon">${canonLead} &middot; ${(m.hex.measured_fraction * 100).toFixed(0)}% MEASURED GROUND${exagBadge}</div>
        ${fromPyramid ? `<div class="atlas-card-hint">This body has no surveyed region, so the
          ground under the walkaround is its own height pyramid resampled where the wheel came
          down. Cut a finer one with <code>python3 tools/dtm/bake_region.py --body ${
          escapeHtml(state.bodyId ?? '')} --slug &lt;slug&gt;</code>.</div>` : ''}

        ${deps.live.selectedSub ? sect('cell', `CELL <span class="atlas-dim">${escapeHtml(deps.live.selectedSub.id)}</span>`, renderSelectedCell(deps.live.selectedSub, climate)) : '<div class="atlas-card-hint">Double-click to select a cell.</div>'}

        ${sect('land', 'BUILDABLE LAND', `
          <div class="atlas-card-row"><span>cells</span><span>${deps.live.plotSubs.length} surveyed</span></div>
          <div class="atlas-card-row"><span><i class="v-prime"></i>prime</span><span>${counts['prime']}</span></div>
          <div class="atlas-card-row"><span><i class="v-workable"></i>workable</span><span>${counts['workable']}</span></div>
          <div class="atlas-card-row"><span><i class="v-marginal"></i>marginal</span><span>${counts['marginal']}</span></div>
          <div class="atlas-card-row"><span><i class="v-unbuildable"></i>unbuildable</span><span>${counts['unbuildable']}</span></div>
          <div class="atlas-card-row"><span>yield</span><span>${((buildable / Math.max(1, deps.live.plotSubs.length)) * 100).toFixed(0)}% of the region</span></div>
          ${best ? `<div class="atlas-card-row"><span>best cell</span><span>${best.score} &middot; ${best.meanSlopeDeg.toFixed(1)}&deg; &middot; ${(best.insolationWhSol / 1000).toFixed(2)} kWh/m&sup2;/sol</span></div>` : ''}`)}

        ${sect('evidence', 'EVIDENCE', `
          <div class="atlas-card-row"><span>lat / lon</span><span>${m.center_lat_deg.toFixed(3)}&deg; / ${m.center_lon_deg.toFixed(3)}&deg;</span></div>
          <div class="atlas-card-row"><span>region</span><span>${m.hex.area_km2.toFixed(1)} km&sup2; &middot; ${(m.hex.across_corners_m / 1000).toFixed(2)} km across</span></div>
          <div class="atlas-card-row"><span>elevation</span><span>${m.elevation.min_m.toFixed(0)} … ${m.elevation.max_m.toFixed(0)} m</span></div>
          <div class="atlas-card-row"><span>relief</span><span>${m.elevation.relief_m.toFixed(0)} m</span></div>
          <div class="atlas-card-row"><span>sampling</span><span>${m.effective_resolution_m_per_px.toFixed(1)} m/px (src ${m.source_resolution_m_per_px.toFixed(1)})</span></div>
          ${renderProvenanceRows(m)}
          ${renderHonestyRows()}
          <div class="atlas-card-row"><span>local time</span><span>${solClockLabel(solar.solPhase)} &middot; sol</span></div>
          <div class="atlas-card-row"><span>sun</span><span>${((alt * 180) / Math.PI).toFixed(1)}&deg; alt / ${((az * 180) / Math.PI).toFixed(0)}&deg; az</span></div>
          <div class="atlas-card-row"><span>season</span><span>Ls ${solar.lsDeg.toFixed(0)}&deg; &middot; dec ${dec.toFixed(1)}&deg;</span></div>
          <div class="atlas-card-row"><span>irradiance</span><span>${flux.toFixed(0)} W/m&sup2; <span class="atlas-dim">(mean ${climate.fluxMeanWm2})</span></span></div>
          <div class="atlas-card-row"><span>dust</span><span>${tau === null ? 'not modelled' : `&tau; ${tau.toFixed(2)}`}</span></div>
          <div class="atlas-card-hint" style="margin:0">
            ${escapeHtml(m.provenance.instrument)} &middot; ${escapeHtml(m.provenance.product_id)}<br>
            ${escapeHtml(m.provenance.rights)}
          </div>`)}
      </div>`;
  }

  /**
   * The selected cell. Every figure here is measured off the same array the
   * shader displaces — which is the whole point: the shadow you can see falling
   * across this hex is the reason its sun fraction reads what it reads.
   */
  function renderSelectedCell(p: SubPlot, climate: PlotClimate): string {
    const tierLabels: Record<DataTier, string> = {
      0: 'ORBIT', 1: 'STEREO', 2: 'SURVEYED',
    };
    const tierClasses: Record<DataTier, string> = {
      0: 'tier-orbit', 1: 'tier-stereo', 2: 'tier-surveyed',
    };
    const band = bandFor(p);
    const compass = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'][
      Math.round(p.aspectDeg / 45) % 8
    ];
    const yd = cellYield(p, solar.lsDeg, climate, allStructures());
    const gfLabel = yd.gradeFactor === Infinity ? '&infin;' : `&times;${yd.gradeFactor.toFixed(2)}`;
    const cableNote = yd.cableBlocked
      ? `<span class="atlas-warn">${escapeHtml(yd.cableBlocked)}</span>`
      : `&lt;${CABLE_M} m`;

    // Tier badge
    const tierBadge = `<span class="atlas-tier-badge ${tierClasses[p.dataTier]}">${tierLabels[p.dataTier]}</span>`;

    // Verdict row: exact at tier 2, band at tier 0/1
    const verdictRow = p.dataTier === 2
      ? `<div class="atlas-card-row"><span><i class="v-${p.verdict}"></i>verdict</span><span>${p.verdict.toUpperCase()} &middot; ${p.score}</span></div>`
      : `<div class="atlas-card-row"><span><i class="v-${band.verdictBand}"></i>verdict</span><span>${escapeHtml(band.label)}</span></div>`;

    // At tier 0/1, most evidence rows are hidden — the player only sees the band.
    const evidenceRows = p.dataTier === 2 ? `
      <div class="atlas-card-row"><span>kW / panel</span><span>${yd.kWPerPanel.toFixed(2)} kW</span></div>
      <div class="atlas-card-row"><span>workability</span><span>${(yd.workability * 100).toFixed(0)}%</span></div>
      <div class="atlas-card-row"><span>grade factor</span><span>${gfLabel}</span></div>
      <div class="atlas-card-row"><span>cable</span><span>${cableNote}</span></div>
      <div class="atlas-card-row"><span>area</span><span>${(2.598076 * (p.radius_m / 1000) ** 2).toFixed(2)} km&sup2;</span></div>
      <div class="atlas-card-row"><span>elevation</span><span>${p.elevation_m.toFixed(0)} m</span></div>
      <div class="atlas-card-row"><span>slope</span><span>${p.meanSlopeDeg.toFixed(1)}&deg; mean &middot; ${p.maxSlopeDeg.toFixed(1)}&deg; max</span></div>
      <div class="atlas-card-row"><span>relief</span><span>${p.relief_m.toFixed(1)} m</span></div>
      <div class="atlas-card-row"><span>roughness</span><span>${p.roughness_m.toFixed(2)} m rms</span></div>
      <div class="atlas-card-row"><span>aspect</span><span>${compass} &middot; ${p.aspectDeg.toFixed(0)}&deg;</span></div>
      <div class="atlas-card-row"><span>sun kept</span><span>${(p.sunFraction * 100).toFixed(0)}% of daylight</span></div>
      <div class="atlas-card-row"><span>insolation</span><span>${(p.insolationWhSol / 1000).toFixed(2)} kWh/m&sup2;/sol</span></div>
      <div class="atlas-card-row"><span>cell across</span><span>${(p.radius_m * 2).toFixed(0)} m &middot; grid 100 m</span></div>
      <div class="atlas-card-row"><span>${REFERENCE_MW} MW array</span><span>${Math.sqrt(arrayAreaFor(referenceWhSol(climate), p.insolationWhSol)).toFixed(0)} m square</span></div>
      <div class="atlas-card-row"><span>Solar I here</span><span>${(arrayAreaFor(PV.solarOneWhSol, p.insolationWhSol) / 1e6).toFixed(1)} km&sup2; &middot; ${Math.ceil(arrayAreaFor(PV.solarOneWhSol, p.insolationWhSol) / (2.598076 * p.radius_m * p.radius_m))} cells</span></div>`
      : `<div class="atlas-card-row"><span>elevation</span><span>${p.elevation_m.toFixed(0)} m</span></div>
      <div class="atlas-card-row"><span>area</span><span>${(2.598076 * (p.radius_m / 1000) ** 2).toFixed(2)} km&sup2;</span></div>
      <div class="atlas-card-row"><span>cell across</span><span>${(p.radius_m * 2).toFixed(0)} m</span></div>
      <div class="atlas-card-hint">Deploy the rover to survey this cell and reveal exact measurements.</div>`;

    // Survey button for unsurveyed cells
    const surveyBtn = p.dataTier < 2
      ? '<button class="atlas-survey-btn" data-survey="1">SURVEY TO CONFIRM</button>'
      : '';

    return `
      ${renderCellBuildings(p)}
      <div class="atlas-card-row"><span>data</span><span>${tierBadge}</span></div>
      ${verdictRow}
      ${evidenceRows}
      ${surveyBtn}
      ${renderParcel(p)}
      ${renderDescend(p)}
      <div class="atlas-card-hint">Escape clears this selection. Double-click the cell again, or
        any ground outside it, to pick another.</div>`;
  }

  /**
   * What stands on this cell, read off the crafting state: each building's
   * class and level, its slots, what it is doing, and a way into the Forge
   * landed on that building. Nothing when the cell is bare.
   */
  function renderCellBuildings(sub: SubPlot): string {
    const cellId = deps.siteGround.cellOfSub(sub);
    const rows = cellId ? deps.cellBuildings(cellId) : [];
    if (rows.length === 0) return '';
    const body = rows.map((r) => {
      const st = r.status;
      const doing = st.mode === 'active'
        ? `&#9679; ${escapeHtml(st.jobName ?? 'job')} &middot; ${formatEta(st.etaMs ?? 0)}`
        : st.mode === 'paused'
          ? `<span class="atlas-warn">PAUSED &middot; ${escapeHtml(st.pauseReason ?? '')}</span>`
          : st.mode === 'ready' ? 'READY TO CLAIM' : r.kind === 'solar' ? 'FEEDS THE GRID' : 'IDLE';
      const ready = r.readyJobs > 0 ? `<span class="atlas-bldg-ready">&#10003; ${r.readyJobs} READY</span>` : '';
      const slots = st.slotsMax > 0 ? `SLOTS ${st.slotsUsed}/${st.slotsMax} &middot; ` : '';
      const open = r.buildingId
        ? `<button class="atlas-forge-btn" data-forge-building="${escapeHtml(r.buildingId)}">OPEN IN FORGE</button>`
        : '';
      return `<div class="atlas-bldg" data-cell-building="${escapeHtml(r.buildingId ?? r.kind)}">
        <div class="atlas-bldg-head"><b>${escapeHtml(r.name)}</b>
          <span class="atlas-dim">${escapeHtml(st.className.toUpperCase())}${r.level ? ` &middot; L${r.level}` : ''}</span></div>
        <div class="atlas-bldg-line">${slots}${doing} ${ready}</div>
        ${open}
      </div>`;
    }).join('');
    return `<div class="atlas-bldgs"><div class="atlas-bldgs-title">BUILDINGS ON THIS HEX &middot; ${rows.length}</div>${body}</div>`;
  }

  /**
   * The plot this cell belongs to: its name, its address, and who holds it.
   *
   * This is the section the whole tier was missing. Everything above it
   * describes GROUND; this describes PROPERTY.
   */
  function renderParcel(cell: SubPlot): string {
    const par = parentOf(cell);
    if (!par) return '';
    const held = par.claim;
    // Check whether this plot has at least one surveyed cell — a stake requires it.
    const plotCells = deps.live.plotSubs.filter(
      (s) => s.parentQ === cell.parentQ && s.parentR === cell.parentR,
    );
    const hasSurveyed = plotCells.some((s) => s.dataTier === 2);
    const stakeDisabled = !hasSurveyed && !held;
    // Gated on the pin sitting inside the loaded site's own hex grid, not a fixed arc
    // of its centre. `cell` is still the nearest surveyed plot, not the hull's own texel.
    const unsurveyed = groundUnsurveyed();
    return `
      <div class="atlas-parcel">
        <div class="atlas-parcel-name">${unsurveyed ? 'Unsurveyed ground' : escapeHtml((par.cellAddr ?? par.addr).name)}</div>
        <div class="atlas-card-row"><span>address</span><span>${(par.cellAddr ?? par.addr).lat.toFixed(3)}&deg;N ${(par.cellAddr ?? par.addr).lon.toFixed(3)}&deg;E</span></div>
        ${par.cellAddr && par.cellAddr.cell !== par.addr.cell
          ? `<div class="atlas-card-row"><span>plot</span><span>${escapeHtml(par.addr.name)}</span></div>`
          : ''}
        <div class="atlas-card-row"><span>H3 cell</span><span class="atlas-mono">${escapeHtml((par.cellAddr ?? par.addr).cell)}</span></div>
        <div class="atlas-card-row"><span>plots</span><span>${parcelsIn(deps.live.plotField!.meta.hex.area_km2, groundDatumM(state.bodyId))} &times; ${parcelAreaKm2(groundDatumM(state.bodyId)).toFixed(2)} km&sup2;</span></div>
        <div class="atlas-card-row"><span>quadrangle</span><span>${escapeHtml(quadFor(par.lat, par.lon)?.id ?? '—')} ${escapeHtml(quadFor(par.lat, par.lon)?.name ?? '')}</span></div>
        <div class="atlas-card-row"><span>ground</span><span class="atlas-dim">${escapeHtml(par.place.source_product ?? sourceLabelFor(par.place.source))}</span></div>
        <div class="atlas-card-row"><span>status</span><span>${
          unsurveyed
            ? '<span class="atlas-dim">Unsurveyed</span>'
            : held
              ? `<span class="atlas-held">HELD</span> &middot; ${escapeHtml(held.holder)}`
              : '<span class="atlas-dim">Unclaimed</span>'
        }</span></div>
        ${
          unsurveyed
            ? `<div class="atlas-card-hint">The ground under the ship has not been surveyed. This `
              + `card names ${escapeHtml(par.addr.name)}, the nearest surveyed plot, not the ground `
              + `under the hull.</div>`
              + '<button class="atlas-claim" disabled title="Unsurveyed ground cannot be staked">&#9670; STAKE THIS PARCEL</button>'
            : held
              ? `<div class="atlas-card-row"><span>staked</span><span class="atlas-dim">Sol ${held.sol}</span></div>
                 <button class="atlas-claim release" data-release="1">RELINQUISH</button>`
              : stakeDisabled
                ? '<button class="atlas-claim" disabled title="Survey at least one cell in this plot before staking">&#9670; STAKE THIS PARCEL</button><div class="atlas-card-hint">Survey at least one cell inside this plot before staking.</div>'
                : '<button class="atlas-claim" data-claim="1">&#9670; STAKE THIS PARCEL</button>'
        }
      </div>`;
  }

  /**
   * Descent is only offered where there IS something finer to arrive at. A plot
   * beyond the stereo footprint is already shown at everything known about it;
   * magnifying the same MOLA would be a promise the data cannot keep.
   */
  function renderDescend(p: SubPlot): string {
    if (state.tier !== 5 || p.parentQ === undefined) return '';
    const place = deps.live.plotField?.placements.find(
      (x) => x.q === p.parentQ && x.r === p.parentR,
    );
    if (place && place.tile === null) {
      // Say how to find one, not only why this is not it. Nineteen of Jezero's
      // thirty-six plots land here, so "no" on its own sends you clicking.
      const n = deps.live.plotField?.placements.filter((x) => x.tile).length ?? 0;
      const total = deps.live.plotField?.placements.length ?? 0;
      return `<div class="atlas-card-hint">No detail tile: this plot lies beyond the
        stereo footprint, so the region already holds everything measured about it.
        <br><strong>${n} of ${total}</strong> plots here can be entered — they carry a
        <span style="color:#7fb8cc">cool inner edge</span>.</div>`;
    }
    return `<button class="atlas-descend" data-tile="${p.parentQ}_${p.parentR}">&#9660; ENTER PLOT ${p.parentQ}, ${p.parentR}</button>`;
  }

  function crumb(label: string, active: boolean, onClick?: () => void): string {
    const cls = active ? 'atlas-bc-seg active' : 'atlas-bc-seg link';
    const tag = onClick ? 'a' : 'span';
    return `<${tag} class="${cls}">${escapeHtml(label)}</${tag}>`;
  }

  /**
   * A rung the ladder is not at yet, and whether it is one this body can reach.
   *
   * The same ghost as before for a rung further down, struck through with the
   * reason for one under an imagery-only body's floor - the ladder is the same
   * eight rungs on every body and this is where it says which of them this
   * body's data does not carry.
   */
  function ghostCrumb(label: string, tier: number): string {
    const floorM = descentFloorM(state.bodyId);
    // A corridor body has no ground at any rung, so the test is the class and
    // not the rung: Neptune's floor is 45,000 m, which reads as tier 4, and
    // Survey went unstruck on a body that can never have one.
    const corridor = corridorFor(state.bodyId) !== null;
    if (floorM === null || (!corridor && tier <= floorTier())) {
      return `<span class="atlas-bc-seg ghost">${label}</span>`;
    }
    const name = rowById(state.bodyId)?.display_name ?? state.bodyId;
    const why = corridor
      ? `no surface on ${name}: the descent stops at the cloud-top corridor's lowest band edge, `
        + `${(floorM / 1000).toFixed(0)} km above the datum`
      : `no elevation for ${name}: the descent stops at the imagery floor, `
        + `${(floorM / 1000).toFixed(0)} km above the datum`;
    return `<span class="atlas-bc-seg ghost beyond" title="${escapeHtml(why)}">${label}</span>`;
  }

  /**
   * The corridor's three bands as this body's own rungs.
   *
   * They stand where a class (a) body's cell, survey and region rungs stand:
   * the ladder below is struck through by `ghostCrumb`, and these are what a
   * class (c) body has instead. Titled with their own edges off the row, so a
   * moved edge is visible in the breadcrumb without a second derivation.
   */
  function corridorCrumbs(): string[] {
    const geo = corridorFor(state.bodyId);
    if (geo === null) return [];
    const here = corridorBandIndexAt(geo, altitude());
    return geo.bands.map((b, i) => {
      const cls = i === here ? 'atlas-bc-seg active' : 'atlas-bc-seg ghost band';
      const he3 = b.he3MoleFraction === null
        ? 'no He-3 mole fraction is published for this body'
        : `He-3 ${(b.he3MoleFraction * 1e6).toFixed(1)} ppm`;
      const why = `${b.bottomAltitudeM} to ${b.topAltitudeM} m above the datum; ${he3}`;
      return `<span class="${cls}" title="${escapeHtml(why)}">${escapeHtml(b.title)}</span>`;
    });
  }
  /**
   * Cap the inspector at a whole row, never through one.
   *
   * `--hud-insp` is a budget, not a row grid: a card whose rows land past it
   * used to clip mid-sentence, which is how a ring row 6.9x too bright to miss
   * read as half a word instead. This measures the rendered card's own
   * children and caps the box at the last one that fits, so a row that does
   * not fit scrolls cleanly out of view instead of being cut through the
   * middle. Tightening row spacing instead would still land mid-row for a
   * tall enough card, just a different one; this is the shape that cannot.
   *
   * `[data-fold-anchor]` (only the rings row, on a body that has one) is a
   * floor under the budget rather than a second cap: the honesty block a
   * body's row can add below it is free to scroll away (it already did,
   * quietly, before this row existed), but the ring note is not one more
   * row to lose to a fixed pixel count picked before it existed. Scoped to
   * that one row rather than every identity row above it - marking
   * "ground sample" too once pushed a body with no ring note past the
   * manifest panel's own footprint at 800x500, the one state that reaches
   * this fold with the manifest already expanded.
   */
  function snapInspectorFold(): void {
    const card = elInspector.querySelector<HTMLElement>('.atlas-card');
    if (!card || card.children.length === 0) { elInspector.style.maxHeight = ''; return; }
    const cssBudget = parseFloat(getComputedStyle(overlay).getPropertyValue('--hud-insp')) || 228;
    // Measured against the box's own natural height, not last frame's capped
    // one — reading a rect through an existing cap would find every row
    // "fits" because the cap already hid the ones that don't.
    elInspector.style.maxHeight = 'none';
    const top = elInspector.getBoundingClientRect().top;
    const anchors = card.querySelectorAll<HTMLElement>('[data-fold-anchor]');
    const lastAnchor = anchors[anchors.length - 1];
    const floor = lastAnchor ? lastAnchor.getBoundingClientRect().bottom - top : 0;
    const budget = Math.max(cssBudget, floor);
    let last = 0;
    let fits = true;
    for (const child of Array.from(card.children) as HTMLElement[]) {
      const bottom = child.getBoundingClientRect().bottom - top;
      if (bottom > budget) { fits = false; break; }
      last = bottom;
    }
    // Every row fit (inside the budget the floor may have raised): the CSS
    // default governs, same as a card too short to ever hit this function.
    // Rounded up rather than down: `last` is a row this box must NOT cut, and
    // the next excluded child starts a 5px flex gap later, well clear of
    // rounding up by under a pixel.
    elInspector.style.maxHeight = fits ? '' : `${Math.ceil(last)}px`;
  }
  function tierHint(): string {
    // A class (c) body's whole ladder: there is no rung below the corridor, so
    // the hint says which band the camera is in at every one of them.
    const corridor = corridorFor(state.bodyId);
    if (corridor !== null) {
      const here = corridorBandIndexAt(corridor, altitude());
      const name = rowById(state.bodyId)?.display_name ?? state.bodyId ?? 'this body';
      const at = here === null
        ? `above the corridor's top at ${Math.round(corridor.topAltitudeM / 1000)} km`
        : `in the ${corridor.bands[here]!.title} band`;
      return `<strong>Drag</strong> to orbit &middot; <strong>scroll</strong> to descend &middot; `
        + `${escapeHtml(name)} is a cloud-top corridor with no surface: ${escapeHtml(at)}, and the `
        + `descent stops ${Math.round(corridor.floorAltitudeM / 1000)} km above the datum.`;
    }
    // The bottom of an imagery-only body's ladder: the rung the data floor
    // stops at, whichever rung that turns out to be.
    const floor = imageryFloor(state.bodyId);
    if (floor !== null && descentFloorM(state.bodyId) !== null && state.tier >= floorTier()) {
      const name = rowById(state.bodyId)?.display_name ?? state.bodyId ?? 'this body';
      return `<strong>Drag</strong> to orbit &middot; the descent stops here: ${escapeHtml(name)} `
        + `is imaged at ${mpp(floor.gsdM)} and has no elevation at any level.`;
    }
    if (state.tier === 1) {
      const base = '<strong>Drag</strong> to orbit · <strong>scroll</strong> to zoom';
      // No quadrangle layer to reveal on a body that publishes none, or that
      // has no ground to descend to.
      return descendable() && bodyQuads().length > 0
        ? `${base} · <strong>double-click</strong> to map quadrangles.`
        : `${base}.`;
    }
    if (state.tier === 2) {
      const q = availableQuad();
      const target = q ? `${q.id} ${q.name}` : 'the surveyed quadrangle';
      return `<strong>Drag</strong> to orbit · <strong>scroll</strong> to descend · <strong>double-click ${escapeHtml(target)}</strong> (cyan) to jump to it.`;
    }
    if (state.tier === 3) return '<strong>Drag</strong> to orbit · <strong>scroll</strong> to descend · <strong>double-click the highlighted cell</strong> (gold) to jump to the Survey.';
    if (state.tier === 4) return '<strong>Drag</strong> to orbit · <strong>scroll</strong> to descend · <strong>double-click the highlighted cell</strong> (gold) to jump to the terrain.';
    if (state.tier === 5) return 'Digital Terrain Model · <strong>scroll</strong> to descend · <strong>double-click</strong> a plot to select it · <kbd>Esc</kbd> deselects, then ascends.';
    if (state.tier >= 6) {
      const look = deps.cameraRig.plotCamera() || deps.cameraRig.tileWalkaround() ? ' · <strong>drag up</strong> to look at the sky' : '';
      return `<kbd>WASD</kbd> move · <kbd>R</kbd>/<kbd>F</kbd> up/down · <kbd>Shift</kbd> sprint${look} · <strong>scroll</strong> to zoom · <kbd>Esc</kbd> deselects, then ascends.`;
    }
    return '';
  }

  /**
   * The hero's call to action, from the registry's own counts.
   *
   * It named Mars in the markup, then every `availability.descend` row - which
   * was a set of one when that was written and is nine now, four of them
   * satellites picked at their primary's band. And the opening view reaches
   * Neptune, so telling a player to double-click a body 15 px from Sol is not
   * the route any more. It names the panel that is.
   */
  function syncHeroCta(): void {
    // Short on purpose: the hero is centred and full-bleed and the manifest
    // panel is in the left column, so at 800 wide a long line reaches it
    // (`layout-check`'s panel overlap read). The descend count is on the card
    // below, which has a column of its own.
    elHeroCta.innerHTML = deps.bodyScene.state.bodyRows.length === 0
      ? 'The body registry has not landed yet.'
      : `Open <strong>BODIES</strong> to reach any of the ${deps.bodyScene.state.bodyRows.length}.`;
  }

  /**
   * The card for a pick that has nowhere to go.
   *
   * It used to cite a tracker number and erase itself after 2.4 s, so the
   * answer was gone before it had been read. It states which bodies do have
   * ground instead, and stays until the next pick or Escape.
   */
  function flashInspector(msg: string): void {
    const open = deps.bodyScene.state.bodyRows.filter((b) => b.availability.descend).map((b) => b.display_name);
    const openLine = open.length
      ? `${open.join(', ')} ${open.length === 1 ? 'is' : 'are'} the only ${open.length === 1 ? 'body' : 'bodies'} with surveyed ground in this slice.`
      : 'No body has surveyed ground in this slice.';
    deps.overlayView.state.notInSlice = `
      <div class="atlas-card warn">
        <div class="atlas-card-title">NOT IN SLICE</div>
        <div class="atlas-card-row">${escapeHtml(msg)}</div>
        <div class="atlas-card-hint">${escapeHtml(openLine)} Pick again, or press <kbd>Esc</kbd>.</div>
      </div>
    `;
    renderOverlay();
    deps.live.needsRender = true;
  }
  /** Clear the not-in-slice card. */
  function clearFlash(): boolean {
    if (!deps.overlayView.state.notInSlice) return false;
    deps.overlayView.state.notInSlice = null;
    return true;
  }

  // Delegated: renderPlotSampler rewrites its own innerHTML on every survey and
  // every frame of a season sweep, so a per-render binding would pile up.
  function handleSamplerClick(e: MouseEvent): void {
    const head = (e.target as HTMLElement).closest('.atlas-sect-head') as HTMLElement | null;
    if (head) {
      toggleSection(head.dataset['sect'] ?? '');
      head.parentElement?.classList.toggle('closed');
      return;
    }
    const forgeBtn = (e.target as HTMLElement).closest('[data-forge-building]') as HTMLElement | null;
    if (forgeBtn?.dataset['forgeBuilding']) {
      deps.openBuilding(forgeBtn.dataset['forgeBuilding']);
      return;
    }
    const claimBtn = (e.target as HTMLElement).closest('.atlas-claim') as HTMLElement | null;
    if (claimBtn && deps.live.selectedSub) {
      void stakeSelected(Boolean(claimBtn.dataset['release']));
      return;
    }
    // Survey button: dispatch the rover to this cell. The rover drives on the
    // whole-file plot mesh, which only exists below the SURFACE edge, so
    // deploying is what fetches it - the job is enqueued once there is a rover.
    const surveyBtn = (e.target as HTMLElement).closest('.atlas-survey-btn') as HTMLElement | null;
    if (surveyBtn && deps.live.selectedSub) {
      const cell = deps.live.selectedSub;
      const job: SurveyJob = { target: { x: cell.x, z: cell.z }, cellId: cell.id };
      void (async () => {
        if (!deps.roverDrive.state.driving) await setDriving(true);
        if (deps.live.disposed || !deps.live.plot?.rover) return;
        deps.live.plot.rover.enqueueSurvey(job);
        logEvent(`Survey dispatched: cell ${cell.id.slice(-8)}`, cell);
        renderOverlay();
      })();
      return;
    }
    const b = (e.target as HTMLElement).closest('.atlas-descend') as HTMLElement | null;
    if (!b) return;
    state.tier = 6;
    state.tileId = b.dataset['tile'] ?? null;
    // Framed on the cell the button was offered on, not on the claim's centre.
    // The PLOT rung stands the eye about 870 m back along the ground from
    // whatever it aims at, so a cell a kilometre off that centre opened with
    // the frame north of it. Below the SURFACE edge the selection lerp in the
    // frame loop already does this.
    applyState({ aim: selectedCellLatLon() });
  }

  return {
    honestyDebug,
    ringGainNote,
    rockLawFor,
    imageryCoverage,
    manifestReading,
    renderHonestyRows,
    renderPlotSampler,
    renderParcel,
    crumb,
    ghostCrumb,
    corridorCrumbs,
    snapInspectorFold,
    tierHint,
    syncHeroCta,
    flashInspector,
    clearFlash,
    handleSamplerClick,
    probe: {
      /** `descent-dojo --artificial`: what the fill covers on this body, and the
       *  one control that makes it paint over measured ground. */
      artificialReading(): { share: number | null; basis: string; shown: boolean } | null {
        return honestyReading()?.artificial ?? null;
      },
      /** `descent-dojo --artificial`'s relief leg: what this body's own relief
       *  field carries, and whether the frame is shading by it. */
      reliefReading(): {
        amplitudeM: number; capM: number; basis: string; shown: boolean;
      } | null {
        return honestyReading()?.relief ?? null;
      },
      get honestyDebug(): unknown {
        return {
          forced: honestyDebug.kSource,
          claimRelief: honestyDebug.claimRelief,
          creditBlank: honestyDebug.creditBlank,
          reading: honestyReading(),
        };
      },
    },
  };
}
